<template>
  <div class="paypal-tab">
    <div v-if="canCheckout">
      <h3>Continue to Paypal to complete your purchase</h3>
      <div
        class="disclaimer"
        v-if="productType === availableProductTypes.COACH_SUBSCRIPTION"
      >
        By clicking "Proceed", you agree to enroll in our {{ billedIntervalAdjective }} subscription
        plan and to our Terms of Service. Your payment method will be charged the
        price above the first {{ billedInterval }} and {{ billedIntervalAdverb }} thereafter at the then-current
        rate. Prices are tax inclusive. Cancel any time in Settings. No refunds
        for partial unused periods.
      </div>
      <p
        v-else-if="productType === availableProductTypes.MEMBERSHIP_SUBSCRIPTION"
      >
        By completing your order through PayPal, you agree to enroll in our {{ billedIntervalAdjective }}
        subscription plan and to our Terms of Service. Your payment method will be
        charged the price above the first {{ billedInterval }} and {{ billedIntervalAdverb}} thereafter at the
        then-current rate. Prices are tax inclusive. Cancel any time in Settings.
        No refunds for partial unused periods, or after gift is redeemed.
      </p>
      <div v-if="paypalError" class="error">
        {{ paypalError }}
      </div>
      <div class="error" v-show="error === 'paypal_setup_fee_failure'">
        PayPal payment declined. Please try again or use a card.
      </div>
      <div class="error" v-show="error === 'trial_ineligibility'">
        This card is not eligible for a trial.
      </div>
      <div v-if="trialIneligibility" class="trial-ineligibility">
        You can still enjoy all of the benefits of our Pro Membership without a
        trial period. You will be charged [${{ subscriptionInitialPrice }}
        {{ subscriptionType }}].
      </div>
      <button @click="beginPayPalCheckout">
        <lottie
          v-if="paypalSubmitting"
          :options="{ animationData: authLoading }"
          :width="24"
          :height="24"
        />
        <img v-else src="@/assets/images/payment/paypal_2.png" alt="paypal" />
      </button>
    </div>
    <div v-else>
      <div v-if="hasIncompatibleCode" class="error">
        The applied promotion is not supported by this payment method. Please select a different option.
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"
import Lottie from "vue-lottie"
import authLoading from "@/assets/animations/home/authLoading.json"
import { PRODUCT_TYPE } from "@/constants/payment"
import {
  INTERVAL_TYPE_ADJECTIVES,
  INTERVAL_TYPE_ADVERBS,
  PROMO_CODE_TYPES,
} from "@/store/payment/payment.constants"
import { getSubscriptionProductStoreDetails } from "@/store/store/store.utils"

export default {
  components: {
    Lottie,
  },
  props: {
    productType: {
      type: String,
      default: PRODUCT_TYPE.POINT_PURCHASE,
    },
    paypalQueryString: {
      type: String,
    },
    promoCodes: {
      type: Array,
      default: () => {
        return []
      },
    },
  },
  data: function () {
    return {
      error: null,
      authLoading,
      availableProductTypes: PRODUCT_TYPE,
    }
  },
  computed: {
    ...mapGetters("User", ["currentActiveGame"]),
    ...mapGetters("Store/Gift", ["currentGift"]),
    ...mapGetters("PaymentLegacy", ["trialIneligibility"]),
    ...mapGetters("Store", [
      "paypalSubmitting",
      "paypalError",
      "selectedProduct",
      "subscriptionInitialPrice",
      "subscriptionType",
    ]),
    isSubscription () {
      return [
        this.availableProductTypes.MEMBERSHIP_TRIAL,
        this.availableProductTypes.MEMBERSHIP_SUBSCRIPTION,
        this.availableProductTypes.COACH_SUBSCRIPTION,
      ].includes(this.productType)
    },
    promoCodesForCheckout () {
      return this.promoCodes.map(promoCode => {
        return promoCode.code
      })
    },
    hasIncompatibleCode () {
      return this.promoCodes.reduce((hasIncompatibleCode, promoCode) => {
        return hasIncompatibleCode || promoCode.type === PROMO_CODE_TYPES.PRODUCT_DISCOUNT
      }, false)
    },
    canCheckout () {
      return !this.hasIncompatibleCode
    },
    subscriptionProductDetails () {
      return getSubscriptionProductStoreDetails(this.selectedProduct)
    },
    billedInterval () {
      return this.subscriptionProductDetails?.billedInterval
    },
    billedIntervalAdverb () {
      return INTERVAL_TYPE_ADVERBS[this.billedInterval]
    },
    billedIntervalAdjective () {
      return INTERVAL_TYPE_ADJECTIVES[this.billedInterval]
    },
  },
  methods: {
    ...mapActions("Store", ["payWithPaypal", "subscribeWithPaypal"]),
    ...mapMutations("Store", ["setPaypalRedirectQueryString"]),
    checkPaypalError () {
      this.error = this.$route.query["error"]
    },
    beginPayPalCheckout () {
      if (!this.canCheckout) {
        return
      }

      if (this.paypalQueryString) {
        this.setPaypalRedirectQueryString(this.paypalQueryString)
      }
      if (!this.paypalSubmitting) {
        this.$emit("trackPaymentEvents", "Paypal")
        if (this.isSubscription) {
          this.subscribeWithPaypal({
            promoCodes: this.promoCodesForCheckout,
          })
        } else if (this.productType === PRODUCT_TYPE.GIFT_PURCHASE) {
          this.payWithPaypal({
            product: this.currentGift.product,
            orderType: "gift",
          })
        } else {
          this.payWithPaypal({
            product: this.selectedProduct,
          })
        }
      }
    },
  },
  created () {
    this.checkPaypalError()
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/_mixins.scss";
.paypal-tab {
  font-family: Proxima Nova;
  color: #3b3b3b;
  padding: 0 4rem;
  text-align: center;
  @include phone-small {
    padding: 0 2rem;
  }
  h3 {
    font-size: 1.6rem;
    line-height: 1.9rem;
    width: 100%;
    margin-bottom: 2.6rem;
    @include phone-down {
      margin-bottom: 1.5rem;
    }
  }
  p {
    font-size: 1.4rem;
    line-height: 1.7rem;
    text-align: left;
    margin: 0 0 3.2rem;
    @include phone-small {
      margin: 0 0 2rem;
    }
  }
  button {
    max-width: 100%;
    display: block;
    width: 37.322rem;
    height: 5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    cursor: pointer;
    outline: none;
    border: none;
    background: #ffc339;
    border-radius: 4px;
    &:hover {
      background: #fccb5a;
    }
  }
  .trial-ineligibility {
    color: #545460;
    font-weight: 300;
    font-size: 1.3rem;
    background: #f3f3f3;
    margin-top: 1.5rem;
    margin-bottom: 2rem;
    padding: 1rem;
  }
  .error {
    background: rgba(250, 200, 206, 0.8);
    color: #db1d3f;
    font-style: normal;
    font-weight: 400;
    font-size: 1.3rem;
    padding: 1rem 1.6rem;
    border-radius: 4px;
    text-align: center;
    margin: 1rem 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/payment_v2/PaypalTab.vue